import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { useSelector, useDispatch } from 'react-redux';

import { useEffect } from 'react';

import { Optional } from '@common/typescript/objects/Nullable';

import { CrematoryListInfo, CrematoryServiceTypePreferences } from '@app/objects/Crematory';
import { ServiceType } from '@app/objects/Pet';
import { useCrematory } from '@app/hooks/Crematory/useCrematory';
import { Clinic, getDefaultClinic, isClinicServiceTypeAvailable } from '@app/objects/Clinic';
import { ApplicationState } from '@app/store';
import { getItemActionCreators } from '@app/store/Item';

import { RoleManager } from '@app/services/RoleManager';

export interface ServiceTypeOption {
	label: string;
	value: ServiceType;
}

// Service Type Names

// Private (Individual for Australia)
// SemiPrivate (SemiIndividual for Australia)
// Communal
// Storage
// Products Only

// FORE CREMATORY 19 ONLY!
// Exclusive (Priv)
// Individual (Semi)
// Communal
// Storage
// Products Only

function toServiceTypes(crematory?: CrematoryListInfo): Array<ServiceType> {
	const result: Array<ServiceType> = [];

	if (crematory?.private?.useServiceType) {
		result.push(ServiceType.Private);
	}

	if (crematory?.semiPrivate?.useServiceType) {
		result.push(ServiceType.SemiPrivate);
	}

	if (crematory?.communal?.useServiceType) {
		result.push(ServiceType.Communal);
	}

	if (crematory?.storage?.useServiceType) {
		result.push(ServiceType.Storage);
	}

	if (crematory?.productsOnly?.useServiceType) {
		result.push(ServiceType.ProductsOnly);
	}

	return result;
}

function getTranslationMap(isAustralia: boolean): Map<ServiceType, string> {
	return new Map<ServiceType, string>([
		[ServiceType.Private, `enums.service-type.${isAustralia ? 'aus-' : ''}private`],
		[ServiceType.SemiPrivate, `enums.service-type.${isAustralia ? 'aus-' : ''}semiprivate`],
		[ServiceType.Communal, 'enums.service-type.communal'],
		[ServiceType.Storage, 'enums.service-type.storage'],
		[ServiceType.ProductsOnly, 'enums.service-type.products-only'],
	]);
}

function getServiceTypePreference(crematory: CrematoryListInfo | undefined, serviceType: ServiceType): CrematoryServiceTypePreferences | undefined {
	switch (serviceType) {
	case ServiceType.Private:
		return crematory?.private;
	case ServiceType.SemiPrivate:
		return crematory?.semiPrivate;
	case ServiceType.Communal:
		return crematory?.communal;
	case ServiceType.Storage:
		return crematory?.storage;
	case ServiceType.ProductsOnly:
		return crematory?.productsOnly;
	default:
		return undefined;
	}
}

export function getCrematoryServiceTypeName(crematory: CrematoryListInfo | undefined, serviceType: ServiceType): string | undefined {
	const name = getServiceTypePreference(crematory, serviceType)?.serviceTypeName?.trim();

	return name || undefined;
}

const exclusiveCrematories: Set<number> = new Set([19]);

export function useServiceTypeNames(crematoryId?: Optional<number>, originalNames: boolean = false): Map<ServiceType, string> {
	const crematory = useCrematory(crematoryId);
	const { t } = useTranslation();

	return React.useMemo(() => {
		const crematoryId = crematory?.id ?? 0;

		if (!originalNames && exclusiveCrematories.has(crematoryId)) {
			return new Map<ServiceType, string>([
				[ServiceType.Private, getCrematoryServiceTypeName(crematory, ServiceType.Private) ?? 'Exclusive (Priv)'],
				[ServiceType.SemiPrivate, getCrematoryServiceTypeName(crematory, ServiceType.SemiPrivate) ?? 'Individual (Semi)'],
				[ServiceType.Communal, getCrematoryServiceTypeName(crematory, ServiceType.Communal) ?? 'Communal'],
				[ServiceType.Storage, getCrematoryServiceTypeName(crematory, ServiceType.Storage) ?? 'Storage'],
				[ServiceType.ProductsOnly, getCrematoryServiceTypeName(crematory, ServiceType.ProductsOnly) ?? 'Products Only'],
			]);
		}

		const translationMap = getTranslationMap(!originalNames && crematory?.country?.toLowerCase() === 'aus');
		const result = new Map<ServiceType, string>();

		// eslint-disable-next-line no-restricted-syntax
		for (const [key, value] of translationMap) {
			const serviceTypeName = originalNames ? undefined : getCrematoryServiceTypeName(crematory, key);
			result.set(key, serviceTypeName ?? t(value));
		}

		return result;
	}, [t, crematory, originalNames]);
}

function useClinic(clinicId?: number | null): Clinic | null {
	const clinic = useSelector((state: ApplicationState) => state.clinic);
	const user = useSelector((state: ApplicationState) => state.login.user);
	const dispatch = useDispatch();
	const value = user && RoleManager.for(user.role).isClinicGroup() ? user.clinicId : clinicId;

	useEffect(() => {
		if (!value) return;
		if (clinic.id === value) return;

		const factory = getItemActionCreators<Clinic>();
		dispatch(factory.loadItem('clinic', 'getClinic', value, getDefaultClinic()));
	}, [value, clinic]);

	if (clinic.item?.id !== value) return null;

	return clinic.item;
}

interface Config {
	crematoryId?: Optional<number>;
	clinicId?: Optional<number>;
}

const defaultConfig: Config = {
	crematoryId: undefined,
	clinicId: undefined,
};

export function useServiceTypes({ crematoryId, clinicId }: Config = defaultConfig): Array<ServiceTypeOption> {
	const crematory = useCrematory(crematoryId);
	const clinic = useClinic(clinicId);
	const map = useServiceTypeNames(crematoryId);

	return React.useMemo(() => {
		const items: Array<ServiceTypeOption> = toServiceTypes(crematory)
			.map((type: ServiceType) => ({
				value: type,
				label: map.get(type)!,
			}));

		if (!clinic) return items;

		return items.filter((item: ServiceTypeOption) => isClinicServiceTypeAvailable(clinic, item.value));
	}, [crematory, clinic, map]);
}
